/* Mobile menu scroll-lock (Cornerstone-safe)
   - Locks page background only when the ACTUAL MENU is open
   - Allows native scrolling INSIDE the menu
   - Blocks scroll outside (no background rubber-banding), including iOS long-press cases
   - Broad selector support for Cornerstone menu drawers
*/
(function () {
  'use strict';

  if (window.__MENU_SCROLL_LOCK_SCOPED_V2__) return;
  window.__MENU_SCROLL_LOCK_SCOPED_V2__ = true;

  const html = document.documentElement;
  const body = document.body;

  let locked = false;
  let savedScrollY = 0;
  let drawerRoot = null; // the open menu root element

  // ---- Menu selectors (root + common inner scroll containers) ----
  const MENU_ROOT_SEL = [
    // common
    '.menu-drawer', '#menu-drawer', '#menu', '.mobile-menu',
    '[data-drawer="menu"]', '[data-menu-drawer]', '.drawer--menu', '.menu__drawer',
    'details.menu-drawer', 'details[id*="menu"]',
    'nav[aria-label*="menu"]', 'aside[aria-label*="menu"]',

    // Cornerstone / variants we’ve seen
    '.header__menu-drawer', '.menu-drawer-wrapper', '.site-nav__drawer',
    '.header-drawer', '.navigation-drawer'
  ].join(',');

  const MENU_SCROLL_SEL = [
    // inner wrappers that should scroll
    '.menu-drawer__inner', '.menu__drawer-inner',
    '.menu-drawer__content', '.drawer__inner', '.drawer__content',
    '.menu__content', '.menu__items', '.menu__list', '.menu__panel',
    '.drawer__body', '.drawer__scroll',
    '[data-scrollable]'
  ].join(',');

  const OPENISH_CLASS_RE = /(open|opened|is-open|active|is-active|expanded|shown)/i;

  function isExcludedOverlay(el) {
    if (!el) return false;
    const s = (el.id + ' ' + el.className + ' ' + (el.getAttribute?.('aria-label') || '')).toLowerCase();
    return /\bquick[-\s_]?view\b/.test(s) || /\bproduct[-\s_]?quick\b/.test(s) ||
           /\bsearch\b/.test(s) || /\bcart\b/.test(s) || /\bbasket\b/.test(s) ||
           (/\bmodal\b/.test(s) && !/\bmenu\b/.test(s));
  }

  function isVisible(el) {
    if (!el) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function isMenuElement(el) {
    if (!el || isExcludedOverlay(el)) return false;
    if (el.matches?.(MENU_ROOT_SEL)) return true;

    const id = (el.id || '').toLowerCase();
    const cls = (el.className || '').toString().toLowerCase();
    const aria = (el.getAttribute?.('aria-label') || '').toLowerCase();

    const looksMenu = id.includes('menu') || cls.includes('menu') || aria.includes('menu');
    const looksBad  = /\bquick[-\s_]?view\b/.test(id + ' ' + cls + ' ' + aria) ||
                      /\bsearch\b/.test(id + ' ' + cls + ' ' + aria) ||
                      /\bcart\b/.test(id + ' ' + cls + ' ' + aria);
    return looksMenu && !looksBad;
  }

  function looksOpen(el) {
    if (!el) return false;
    if (el.tagName === 'DETAILS' && el.open) return true;
    if (el.hasAttribute?.('open')) return true;
    if (el.getAttribute?.('aria-expanded') === 'true') return true;
    if (OPENISH_CLASS_RE.test(el.className || '')) return true;
    return false;
  }

  function pickScrollableInner(root) {
    if (!root) return null;
    const candidates = [root, ...root.querySelectorAll(MENU_SCROLL_SEL)];
    for (const el of candidates) {
      const cs = getComputedStyle(el);
      const oy = cs.overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
        return el;
      }
    }
    // fallback: the root itself
    return root;
  }

  function findOpenMenu() {
    // First: explicit roots
    const nodes = document.querySelectorAll(MENU_ROOT_SEL);
    for (const n of nodes) {
      if (isMenuElement(n) && isVisible(n) && looksOpen(n)) return n;
    }
    // Then: any open <details> that look like menu
    const detailsNodes = document.querySelectorAll('details[open]');
    for (const d of detailsNodes) {
      if (isMenuElement(d) && isVisible(d)) return d;
    }
    return null;
  }

  function eventInsideMenuTarget(node) {
    if (!node) return null;
    const inInner = node.closest?.(MENU_SCROLL_SEL);
    const inRoot  = node.closest?.(MENU_ROOT_SEL);
    return inInner || inRoot || null;
  }

  // ---- Lock / unlock ----
  function lock(nextDrawerRoot) {
    if (locked) return;
    drawerRoot = nextDrawerRoot || findOpenMenu();
    locked = true;

    savedScrollY = window.scrollY || window.pageYOffset || 0;
    html.classList.add('menu-open');

    // iOS-safe body lock (prevents background scroll & rubber band)
    Object.assign(body.style, {
      position: 'fixed',
      top: `-${savedScrollY}px`,
      left: '0',
      right: '0',
      width: '100%'
    });
    html.style.scrollBehavior = 'auto';
  }

  function unlock() {
    if (!locked) return;
    locked = false;

    html.classList.remove('menu-open');
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    html.style.scrollBehavior = '';
    window.scrollTo(0, savedScrollY);

    drawerRoot = null;
  }

  // ---- Global guards: block scroll OUTSIDE the menu, allow INSIDE ----
  function onTouchMove(e) {
    if (!locked) return;
    const insideMenu = !!eventInsideMenuTarget(e.target);
    if (!insideMenu) {
      // Outside: block to protect background
      if (e.cancelable) e.preventDefault();
      return;
    }
    // Inside: allow native scrolling (no preventDefault)
    // (iOS: body is fixed, so background cannot move anyway)
  }

  function onWheel(e) {
    if (!locked) return;
    const insideMenu = !!eventInsideMenuTarget(e.target);
    if (!insideMenu) {
      if (e.cancelable) e.preventDefault();
      return;
    }
    // Inside: allow native wheel scrolling
  }

  // Safety net: if background tries to scroll (programmatic or rubber-band), snap back
  let guardRAF = null;
  function onScrollGuard() {
    if (!locked) return;
    const y = window.scrollY || window.pageYOffset || 0;
    if (y !== savedScrollY) {
      if (guardRAF) cancelAnimationFrame(guardRAF);
      guardRAF = requestAnimationFrame(() => window.scrollTo(0, savedScrollY));
    }
  }

  function addBlockers() {
    const CAP = { capture: true, passive: false };
    document.addEventListener('touchmove', onTouchMove, CAP);
    document.addEventListener('wheel', onWheel, CAP);
    document.addEventListener('scroll', onScrollGuard, true);
  }
  function removeBlockers() {
    const CAP = { capture: true, passive: false };
    document.removeEventListener('touchmove', onTouchMove, CAP);
    document.removeEventListener('wheel', onWheel, CAP);
    document.removeEventListener('scroll', onScrollGuard, true);
  }

  // Wire blockers to lock/unlock
  function ensureBlockers() {
    if (locked) addBlockers(); else removeBlockers();
  }

  // ---- Wiring to theme open/close events ----
  function getControlledEls(ctrl) {
    const ids = (ctrl.getAttribute('aria-controls') || '').split(/\s+/).filter(Boolean);
    return ids.map(id => document.getElementById(id)).filter(Boolean);
  }

  function wire() {
    // aria-controls toggles
    document.addEventListener('click', (e) => {
      const t = e.target?.closest?.('[aria-controls]');
      if (!t) return;
      const controlled = getControlledEls(t).filter(isMenuElement);
      if (!controlled.length) return;

      const was = t.getAttribute('aria-expanded');
      if (was == null) return;

      setTimeout(() => {
        const isNowOpen = t.getAttribute('aria-expanded') === 'true';
        if (isNowOpen) lock(controlled[0]); else unlock();
        ensureBlockers();
      }, 0);
    }, true);

    // <details> menus
    document.querySelectorAll('details').forEach((d) => {
      if (!isMenuElement(d)) return;
      if (d.dataset.menuLockDetailsWired) return;
      d.dataset.menuLockDetailsWired = '1';
      d.addEventListener('toggle', () => {
        setTimeout(() => { d.open ? lock(d) : unlock(); ensureBlockers(); }, 0);
      });
    });

    // Class/attribute-based open/close (observer)
    const mo = new MutationObserver(() => {
      const openRoot = findOpenMenu();
      if (openRoot && !locked) { lock(openRoot); ensureBlockers(); }
      if (!openRoot && locked) { unlock(); ensureBlockers(); }
    });
    mo.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open', 'class', 'style', 'aria-expanded', 'hidden', 'inert']
    });

    // ESC support (theme usually handles close; we just unlock if closed)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') setTimeout(() => {
        if (!findOpenMenu()) { unlock(); ensureBlockers(); }
      }, 0);
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();